import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { LEVEL_RANK, resolveLevel, moduleForPath } from "@/admin/modules";

const PermissionsContext = createContext(null);

export function PermissionsProvider({ children }) {
  const { user } = useAuth();
  const [overrides, setOverrides] = useState(null); // null = loading

  const load = useCallback(async () => {
    try {
      const r = await api.get("/me/access-control");
      setOverrides(r.data?.overrides || {});
    } catch {
      setOverrides({});
    }
  }, []);

  useEffect(() => {
    if (user && user.role) load();
  }, [user, load]);

  const levelFor = useCallback(
    (key) => resolveLevel(user?.role, key, overrides || {}),
    [user, overrides],
  );

  // Expose to the axios interceptor (which cannot use React hooks) so it can
  // block write requests that exceed the current module's access level.
  useEffect(() => {
    window.__c7Perms = {
      ready: overrides !== null,
      levelFor: (key) => resolveLevel(user?.role, key, overrides || {}),
    };
    return () => { window.__c7Perms = { ready: false, levelFor: () => "full" }; };
  }, [user, overrides]);

  const can = useCallback((key, action) => {
    const lvl = levelFor(key);
    const need = action === "delete" ? "full" : action === "write" ? "edit" : "view";
    return LEVEL_RANK[lvl] >= LEVEL_RANK[need];
  }, [levelFor]);

  return (
    <PermissionsContext.Provider
      value={{ loading: overrides === null, overrides, levelFor, can, refresh: load }}
    >
      {children}
    </PermissionsContext.Provider>
  );
}

export const usePermissions = () => useContext(PermissionsContext) || {
  loading: false, levelFor: () => "full", can: () => true, refresh: () => {},
};

// Convenience hook for a page: resolves the current route's module level and
// returns view/write/delete capabilities. Use to hide Add/Edit/Save/Delete
// buttons. `full` (incl. super_super_admin) when outside any known module.
export function useCurrentModulePerms() {
  const { levelFor } = usePermissions();
  const location = useLocation();
  const level = levelFor(moduleForPath(location.pathname) || location.pathname);
  return {
    level,
    canView: LEVEL_RANK[level] >= LEVEL_RANK.view,
    canWrite: LEVEL_RANK[level] >= LEVEL_RANK.edit,
    canDelete: LEVEL_RANK[level] >= LEVEL_RANK.full,
    readOnly: LEVEL_RANK[level] < LEVEL_RANK.edit,
  };
}
